import { type ClassValue, clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import { saveAs } from 'file-saver'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Cache expiration time in milliseconds (default: 5 minutes)
const DEFAULT_CACHE_EXPIRY = 5 * 60 * 1000;

// In-memory cache for faster access
const memoryCache: Record<string, { data: any; expiry: number }> = {};

/**
 * Set data in both localStorage and memory cache
 * @param key - Cache key
 * @param data - Data to cache
 * @param expiryMs - Cache expiration time in milliseconds
 */
export function setCache(key: string, data: any, expiryMs = DEFAULT_CACHE_EXPIRY): void {
  const expiry = Date.now() + expiryMs;
  const cacheItem = { data, expiry };
  
  // Save to memory cache
  memoryCache[key] = cacheItem;
  
  // Save to localStorage with expiry
  try {
    localStorage.setItem(key, JSON.stringify(cacheItem));
  } catch (error) {
    console.warn('Failed to save to localStorage:', error);
  }
}

/**
 * Get data from cache (memory first, then localStorage)
 * @param key - Cache key
 * @returns Cached data or null if expired/not found
 */
export function getCache<T>(key: string): T | null {
  // Try memory cache first (faster)
  const memItem = memoryCache[key];
  if (memItem && memItem.expiry > Date.now()) {
    return memItem.data as T;
  }
  
  // If not in memory or expired, try localStorage
  try {
    const stored = localStorage.getItem(key);
    if (!stored) return null;
    
    const item = JSON.parse(stored);
    if (item.expiry > Date.now()) {
      // Refresh memory cache
      memoryCache[key] = item;
      return item.data as T;
    }
    
    // Expired, clean up
    localStorage.removeItem(key);
    return null;
  } catch (error) {
    console.warn('Failed to retrieve from localStorage:', error);
    return null;
  }
}

/**
 * Clear a specific cache item
 * @param key - Cache key to clear
 */
export function clearCache(key: string): void {
  delete memoryCache[key];
  try {
    localStorage.removeItem(key);
  } catch (error) {
    console.warn('Failed to remove from localStorage:', error);
  }
}

/**
 * Clear all cache items with a specific prefix
 * @param prefix - Prefix of cache keys to clear
 */
export function clearCacheByPrefix(prefix: string): void {
  // Clear from memory cache
  Object.keys(memoryCache).forEach(key => {
    if (key.startsWith(prefix)) {
      delete memoryCache[key];
    }
  });
  
  // Clear from localStorage
  try {
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith(prefix)) {
        localStorage.removeItem(key);
      }
    });
  } catch (error) {
    console.warn('Failed to clear localStorage by prefix:', error);
  }
}

/**
 * Clear all cache items from localStorage and memory cache
 * This is a nuclear option for debugging
 */
export function clearAllCache(): void {
  // Clear memory cache
  Object.keys(memoryCache).forEach(key => {
    delete memoryCache[key];
  });

  // Clear localStorage
  try {
    localStorage.clear();
    console.log('All cache cleared from localStorage and memory');
  } catch (error) {
    console.warn('Failed to clear localStorage:', error);
  }
}

/**
 * Clear all template-related cache entries for a specific template
 * This helps ensure comprehensive cache clearing when templates are modified
 */
export function clearTemplateCache(templateId: number | string): void {
  const templateIdStr = String(templateId);

  // Clear template-specific cache keys using the existing clearCacheByPrefix function
  clearCacheByPrefix(`template_${templateIdStr}`);
  clearCacheByPrefix(`template_sections_${templateIdStr}`);
  clearCacheByPrefix(`template_fields_${templateIdStr}`);

  // Also clear the specific cache keys that might exist
  clearCache(`template_${templateIdStr}`);
  clearCache(`template_sections_${templateIdStr}`);
  clearCache(`template_fields_${templateIdStr}`);

  console.log(`Cleared all cache entries for template ${templateIdStr}`);
}

/**
 * Clear all template list cache entries
 * This helps ensure template lists are refreshed after modifications
 */
export function clearTemplateListCaches(projectId?: number): void {
  // Clear global template list cache
  clearCache('templates');

  // Clear project-specific template list cache if projectId provided
  if (projectId) {
    clearCache(`templates_${projectId}`);
  }

  // Clear any template list caches by prefix
  clearCacheByPrefix('templates_');

  console.log(`Cleared template list caches${projectId ? ` for project ${projectId}` : ''}`);
}

/**
 * Download a file generated in memory
 * @param blob - The blob to download
 * @param filename - The filename to use
 */
export function downloadFile(blob: Blob, filename: string): void {
  saveAs(blob, filename);
}
